import type React from 'react';
import { DateField, DateRangePicker, RangeCalendar } from '@heroui/react';
import { parseDate } from '@internationalized/date';
import { I18nProvider } from 'react-aria-components/I18nProvider';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { usePickerLocale } from '@/utils/dateLocale';

type DateRangeFieldProps = {
    ariaLabel: string;
    /** ISO `YYYY-MM-DD`; empty string when unset. */
    fromDate: string;
    toDate: string;
    onChange: (fromDate: string, toDate: string) => void;
    /**
     * Disable the whole control — used while the surface behind it loads, so the
     * field keeps its label and shape instead of being replaced by a skeleton
     * (the repo's loading-state standard).
     */
    isDisabled?: boolean;
    /**
     * Controlled popover state. The Sales period filter opens the calendar when
     * the user picks "Custom" from its shortcut buttons; every other caller
     * leaves this alone and lets the picker own it.
     */
    isOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    /** Stretch the field to its container — the Sales filter bar wants this. */
    fullWidth?: boolean;
    /** testid slot. */
    slot: string;
};

const toRange = (fromDate: string, toDate: string) => {
    if (!fromDate || !toDate) {
        return null;
    }
    try {
        return { start: parseDate(fromDate), end: parseDate(toDate) };
    } catch {
        return null;
    }
};

/**
 * The app's **only** date-range control, built on HeroUI's `DateRangePicker`.
 * Speaks ISO `YYYY-MM-DD` strings in and out; the range calendar is fully
 * keyboard-operable.
 *
 * <p>The locale comes from the active UI language (see `usePickerLocale`) and
 * is applied by the `I18nProvider` wrap right here — without it react-aria
 * falls back to the browser's own locale and renders US `M/D/Y`. That wrap
 * existing in one copy of the picker tree and not the other is exactly how the
 * Sales page came to show `8/17/2026` while every other picker showed
 * `17/08/2026`, so the Sales period filter now renders this component instead
 * of keeping a second copy of the same tree.</p>
 */
export const DateRangeField: React.FC<DateRangeFieldProps> = ({
    ariaLabel,
    fromDate,
    toDate,
    onChange,
    isDisabled = false,
    isOpen,
    onOpenChange,
    fullWidth = false,
    slot,
}) => {
    const locale = usePickerLocale();

    return (
        <I18nProvider locale={locale}>
            <DateRangePicker
                aria-label={ariaLabel}
                isDisabled={isDisabled}
                isOpen={isOpen}
                onChange={(next) =>
                    next && onChange(next.start.toString(), next.end.toString())
                }
                onOpenChange={onOpenChange}
                value={toRange(fromDate, toDate)}
                {...getNodeTestIdProp(`date-range-${slot}`)}
            >
                <DateField.Group fullWidth={fullWidth}>
                    <DateField.Input slot="start">
                        {(segment) => <DateField.Segment segment={segment} />}
                    </DateField.Input>
                    <DateRangePicker.RangeSeparator />
                    <DateField.Input slot="end">
                        {(segment) => <DateField.Segment segment={segment} />}
                    </DateField.Input>
                    <DateField.Suffix>
                        <DateRangePicker.Trigger
                            {...getNodeTestIdProp(`date-range-${slot}-trigger`)}
                        >
                            <DateRangePicker.TriggerIndicator />
                        </DateRangePicker.Trigger>
                    </DateField.Suffix>
                </DateField.Group>
                <DateRangePicker.Popover>
                    <RangeCalendar aria-label={ariaLabel}>
                        <RangeCalendar.Header>
                            <RangeCalendar.YearPickerTrigger>
                                <RangeCalendar.YearPickerTriggerHeading />
                                <RangeCalendar.YearPickerTriggerIndicator />
                            </RangeCalendar.YearPickerTrigger>
                            <RangeCalendar.NavButton slot="previous" />
                            <RangeCalendar.NavButton slot="next" />
                        </RangeCalendar.Header>
                        <RangeCalendar.Grid>
                            <RangeCalendar.GridHeader>
                                {(day) => (
                                    <RangeCalendar.HeaderCell>
                                        {day}
                                    </RangeCalendar.HeaderCell>
                                )}
                            </RangeCalendar.GridHeader>
                            <RangeCalendar.GridBody>
                                {(date) => <RangeCalendar.Cell date={date} />}
                            </RangeCalendar.GridBody>
                        </RangeCalendar.Grid>
                        <RangeCalendar.YearPickerGrid>
                            <RangeCalendar.YearPickerGridBody>
                                {({ year }) => (
                                    <RangeCalendar.YearPickerCell year={year} />
                                )}
                            </RangeCalendar.YearPickerGridBody>
                        </RangeCalendar.YearPickerGrid>
                    </RangeCalendar>
                </DateRangePicker.Popover>
            </DateRangePicker>
        </I18nProvider>
    );
};
